import { useMemo } from "react";
import { useLeadershipDashboardAccess } from "apps/project-iq/hooks/useLeadershipDashboardAccess";
import { usePractices } from "apps/project-iq/hooks/usePractices";
import { usePracticeDashboardWorkspaces } from "apps/project-iq/hooks/usePracticeDashboardWorkspaces";
import { groupWorkspacesByPractice } from "apps/project-iq/utils/groupedWorkspaceDashboardUtils";
import { GroupedWorkspaceDashboard } from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard";
import {
  PRACTICE_ACCESS_DENIED_MESSAGE,
  PRACTICE_ACCESS_ERROR_MESSAGE,
  PRACTICE_DATA_ERROR_MESSAGE,
  PRACTICE_GROUP_EMPTY_STATE_MESSAGE,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.constants";

const PAGE_TITLE = "Practices Dashboard";
const PAGE_DESCRIPTION =
  "Roll-up status across every practice, workspace, and delivery workstream.";

export const PracticesDashboardPage = (): JSX.Element => {
  const {
    hasPracticeAccess,
    hasExecutiveAccess,
    practiceIds,
    isLoading: isAccessLoading,
    isPracticeAccessError,
    isExecutiveAccessError,
  } = useLeadershipDashboardAccess();

  const {
    practices,
    isLoading: isPracticesLoading,
    isError: isPracticesError,
  } = usePractices();

  const {
    workspaces,
    isLoading: isWorkspacesLoading,
    isRefreshing: isWorkspacesRefreshing,
    isError: isWorkspacesError,
  } = usePracticeDashboardWorkspaces({ isEnabled: hasPracticeAccess });

  const accessiblePractices = useMemo(
    () =>
      hasExecutiveAccess
        ? practices
        : practices.filter((practice) => practiceIds.includes(practice.id)),
    [hasExecutiveAccess, practices, practiceIds],
  );

  const practiceGroups = useMemo(
    () => groupWorkspacesByPractice(accessiblePractices, workspaces),
    [accessiblePractices, workspaces],
  );

  const isAccessCheckError = isPracticeAccessError || isExecutiveAccessError;
  const isDataLoading =
    hasPracticeAccess &&
    (isPracticesLoading || isWorkspacesLoading || isWorkspacesRefreshing);
  const isDataError = isPracticesError || isWorkspacesError;

  return (
    <GroupedWorkspaceDashboard
      pageTitle={PAGE_TITLE}
      pageDescription={PAGE_DESCRIPTION}
      isAccessLoading={isAccessLoading}
      isAccessCheckError={isAccessCheckError}
      accessCheckErrorMessage={PRACTICE_ACCESS_ERROR_MESSAGE}
      hasAccess={hasPracticeAccess}
      accessDeniedMessage={PRACTICE_ACCESS_DENIED_MESSAGE}
      isDataLoading={isDataLoading}
      isDataError={isDataError}
      dataErrorMessage={PRACTICE_DATA_ERROR_MESSAGE}
      groups={practiceGroups}
      emptyStateMessage={PRACTICE_GROUP_EMPTY_STATE_MESSAGE}
    />
  );
};
